/**
 * Shopify Cart Quantity Limiter - Ultra Simple Version
 * Prevents duplicate add-to-cart submissions
 */

(function() {
  'use strict';

  console.log('🛒 Cart Limiter Starting...');

  // Create visible debug log on screen
  const DEBUG_VISIBLE = false; // Set to true to show debug log
  let debugLog = null;

  function createVisibleDebugLog() {
    if (!DEBUG_VISIBLE) return;

    debugLog = document.createElement('div');
    debugLog.id = 'visible-debug-log';
    debugLog.style.cssText = `
      position: fixed;
      bottom: 10px;
      left: 10px;
      width: 400px;
      max-height: 300px;
      background: rgba(0, 0, 0, 0.9);
      color: #00ff00;
      font-family: monospace;
      font-size: 11px;
      padding: 10px;
      border-radius: 5px;
      z-index: 999999;
      overflow-y: auto;
      box-shadow: 0 4px 12px rgba(0,0,0,0.5);
      border: 2px solid #00ff00;
    `;

    const header = document.createElement('div');
    header.style.cssText = 'font-weight: bold; margin-bottom: 5px; border-bottom: 1px solid #00ff00; padding-bottom: 5px;';
    header.textContent = '🐛 Cart Debug Log';

    const logContent = document.createElement('div');
    logContent.id = 'debug-log-content';

    debugLog.appendChild(header);
    debugLog.appendChild(logContent);
    document.body.appendChild(debugLog);
  }

  function visibleLog(message) {
    console.log(message);

    if (!DEBUG_VISIBLE) return;

    if (!debugLog) {
      createVisibleDebugLog();
    }

    const logContent = document.getElementById('debug-log-content');
    if (logContent) {
      const entry = document.createElement('div');
      entry.style.cssText = 'margin: 2px 0; padding: 2px; font-size: 10px;';
      const time = new Date().toLocaleTimeString();
      entry.textContent = `[${time}] ${message}`;
      logContent.appendChild(entry);
      logContent.scrollTop = logContent.scrollHeight;

      // Keep only last 20 entries
      while (logContent.children.length > 20) {
        logContent.removeChild(logContent.firstChild);
      }
    }
  }

  visibleLog('🛒 Cart Limiter Starting...');

  // Global submission lock
  let isSubmitting = false;
  let lastSubmitTime = 0;
  const DEBOUNCE_TIME = 2000; // 2 seconds between submissions

  // ============================================================================
  // EXCLUSION CONFIGURATION
  // ============================================================================
  // You can exclude products from quantity limiting in 3 ways:

  // 1. By Individual Product Handle
  // Products excluded from quantity limiting (allow multiple quantities)
  const EXCLUDED_PRODUCTS = [
    'carte-cadeau',  // Gift card
  ];

  // 2. By Collection Handle (Excludes products in specific collections)
  // Collection handles to exclude from quantity limiting
  const EXCLUDED_COLLECTIONS = [
    'ateliers-et-evenements',
  ];

  // Collection IDs to exclude from quantity limiting (optional, backup method)
  const EXCLUDED_COLLECTION_IDS = [
    312569430204,
  ];

  // 3. By Product Tags (EASIEST - Add this tag to products in Shopify admin!)
  // Products with these tags will be excluded
  const EXCLUDED_TAGS = [
    'ateliers',
    'atelier',
    'no-limit',
  ];

  // Store original fetch for checking collections
  const originalFetch = window.fetch;

  // Cache for collection checks to avoid repeated fetches
  const collectionCache = {};

  // Check if a product is excluded from limiting
  async function isProductExcluded(productHandle) {
    // Quick validation
    if (!productHandle || productHandle === '' || productHandle === '/') {
      console.log('⚠️ Invalid product handle, not excluded');
      return false;
    }

    // Check by handle first (fastest)
    if (EXCLUDED_PRODUCTS.includes(productHandle)) {
      console.log('✅ Excluded by handle:', productHandle);
      visibleLog('✅ Excluded by handle');
      return true;
    }

    // Quick check: If current URL contains collection path, use that
    for (const collectionHandle of EXCLUDED_COLLECTIONS) {
      if (window.location.href.includes(`/collections/${collectionHandle}`)) {
        console.log('✅ Excluded by URL collection:', collectionHandle);
        visibleLog('✅ Excluded by URL: ' + collectionHandle);
        return true;
      }
    }

    // Check by tags (fetch product data)
    if (EXCLUDED_TAGS.length > 0) {
      try {
        console.log('🏷️ Checking product tags');
        visibleLog('🏷️ Checking tags');

        const response = await originalFetch(`/products/${productHandle}.js`);
        const productData = await response.json();

        if (productData.tags && Array.isArray(productData.tags)) {
          console.log('📋 Product tags:', productData.tags);

          for (const tag of productData.tags) {
            const tagLower = tag.toLowerCase();
            for (const excludedTag of EXCLUDED_TAGS) {
              if (tagLower === excludedTag.toLowerCase()) {
                console.log('✅ Excluded by tag:', tag);
                visibleLog('✅ Excluded by tag: ' + tag);
                collectionCache[productHandle] = true;
                return true;
              }
            }
          }
        }
      } catch (e) {
        console.log('⚠️ Could not check tags:', e);
      }
    }

    // Check by collection if configured (only if we have collections to check)
    if (EXCLUDED_COLLECTIONS.length === 0 && EXCLUDED_COLLECTION_IDS.length === 0) {
      visibleLog('❌ Not excluded - limiting qty');
      return false;
    }

    // Check cache first
    if (collectionCache[productHandle] !== undefined) {
      console.log('📦 Using cached collection check for:', productHandle);
      return collectionCache[productHandle];
    }

    try {
      // Use current page if we're on it for faster check
      let html;
      if (window.location.pathname.includes(`/products/${productHandle}`)) {
        html = document.documentElement.outerHTML;
        console.log('🔍 Using current page HTML');
        visibleLog('🔍 Using current page');
      } else {
        console.log('🔍 Fetching product page for collection check:', productHandle);
        visibleLog('🔍 Fetching product page...');
        const htmlResponse = await originalFetch(`/products/${productHandle}`);
        html = await htmlResponse.text();
      }

      // Method 1: Check for Shopify product JSON embedded in page
      const productJsonMatch = html.match(/var\s+meta\s*=\s*({[^}]*"product"[^}]*})/);
      if (productJsonMatch) {
        try {
          const meta = JSON.parse(productJsonMatch[1]);
          console.log('📦 Found meta data:', meta);
        } catch (e) {}
      }

      // Method 2: Look for collection links in breadcrumbs (most reliable for Shopify themes)
      // Match breadcrumb structure with collection link
      const breadcrumbMatch = html.match(/<nav[^>]*(?:breadcrumb|Breadcrumb)[^>]*>[\s\S]*?<\/nav>/i);
      if (breadcrumbMatch) {
        const breadcrumbHtml = breadcrumbMatch[0];
        console.log('🍞 Found breadcrumb');
        visibleLog('🍞 Checking breadcrumb');

        for (const collectionHandle of EXCLUDED_COLLECTIONS) {
          // Look for collection link in breadcrumb
          const collectionLinkRegex = new RegExp(`<a[^>]*href=["']/collections/${collectionHandle}(?:/|["'])`);
          if (collectionLinkRegex.test(breadcrumbHtml)) {
            console.log('✅ Excluded by collection in breadcrumb:', collectionHandle);
            visibleLog('✅ Excluded: ' + collectionHandle);
            collectionCache[productHandle] = true;
            return true;
          }
        }
      }

      // Method 3: Check structured data (JSON-LD)
      const jsonLdMatches = html.match(/<script[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi);
      if (jsonLdMatches) {
        for (const scriptTag of jsonLdMatches) {
          const jsonContent = scriptTag.replace(/<script[^>]*>/, '').replace(/<\/script>/, '');
          try {
            const structuredData = JSON.parse(jsonContent);

            // Check BreadcrumbList
            if (structuredData['@type'] === 'BreadcrumbList' && structuredData.itemListElement) {
              console.log('🔍 Checking structured breadcrumbs');
              visibleLog('🔍 Checking breadcrumbs');

              for (const item of structuredData.itemListElement) {
                if (item.item) {
                  const itemUrl = typeof item.item === 'string' ? item.item : item.item['@id'];
                  for (const collectionHandle of EXCLUDED_COLLECTIONS) {
                    if (itemUrl && itemUrl.includes(`/collections/${collectionHandle}`)) {
                      console.log('✅ Excluded by collection:', collectionHandle);
                      visibleLog('✅ Excluded: ' + collectionHandle);
                      collectionCache[productHandle] = true;
                      return true;
                    }
                  }
                }
              }
            }
          } catch (e) {
            console.log('⚠️ Could not parse JSON-LD:', e);
          }
        }
      }

      // Method 4: Check canonical/alternate links that might reference collections
      for (const collectionHandle of EXCLUDED_COLLECTIONS) {
        const collectionPathRegex = new RegExp(`/collections/${collectionHandle}/products/${productHandle}(?:[?#/]|$)`);
        if (collectionPathRegex.test(html)) {
          console.log('✅ Excluded by collection path:', collectionHandle);
          visibleLog('✅ Excluded: ' + collectionHandle);
          collectionCache[productHandle] = true;
          return true;
        }
      }

      // Not excluded
      visibleLog('❌ Not excluded - limiting qty');
      collectionCache[productHandle] = false;
      return false;
    } catch (error) {
      console.error('Error checking product collections:', error);
      // On error, assume not excluded to maintain functionality
      collectionCache[productHandle] = false;
      return false;
    }
  }

  // Store original fetch for other scripts to use
  window.originalFetchUnblocked = window.fetch;
  window.fetch = function(...args) {
    const url = args[0];

    if (typeof url === 'string' && url.includes('/cart/add')) {
      console.log('🚫 Intercepting fetch to /cart/add');

      // Check if this is from the upsell script (unlimited product)
      const isUpsellRequest = args[1]?.headers?.['X-Upsell-Request'] === 'true';

      if (isUpsellRequest) {
        console.log('✅ Upsell request detected, bypassing limiter');
        return originalFetch.apply(this, args);
      }

      const now = Date.now();
      if (now - lastSubmitTime < DEBOUNCE_TIME) {
        console.log('⛔ BLOCKED: Too soon since last submission');
        return Promise.reject(new Error('Please wait before adding another item'));
      }

      if (isSubmitting) {
        console.log('⛔ BLOCKED: Already submitting');
        return Promise.reject(new Error('Already adding to cart'));
      }

      isSubmitting = true;
      lastSubmitTime = now;
      console.log('✅ Allowing fetch request');

      return originalFetch.apply(this, args)
        .then(response => {
          console.log('📦 Fetch completed');
          setTimeout(() => { isSubmitting = false; }, 500);
          return response;
        })
        .catch(error => {
          console.log('❌ Fetch error');
          isSubmitting = false;
          throw error;
        });
    }

    return originalFetch.apply(this, args);
  };

  // Block all form submissions
  function blockDuplicateSubmissions() {
    const forms = document.querySelectorAll('form[action*="/cart/add"]');
    console.log(`📋 Found ${forms.length} cart forms`);

    forms.forEach((form, index) => {
      if (form.dataset.protected) return;
      form.dataset.protected = 'true';

      console.log(`🔒 Protecting form ${index + 1}`);

      // Remove action attribute to force our handler
      const originalAction = form.action;
      form.removeAttribute('action');

      // Intercept all submit events
      const submitHandler = async function(e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        console.log('🖱️ Form submission detected');

        const now = Date.now();
        if (now - lastSubmitTime < DEBOUNCE_TIME) {
          console.log('⛔ BLOCKED: Duplicate submission');
          alert('Please wait a moment before adding another item');
          return false;
        }

        if (isSubmitting) {
          console.log('⛔ BLOCKED: Already processing');
          return false;
        }

        isSubmitting = true;
        lastSubmitTime = now;
        console.log('✅ Processing submission');

        // Get form data
        const formData = new FormData(form);
        const button = form.querySelector('button[type="submit"], input[type="submit"], [name="add"]');

        // Store original button text before changing it
        let originalText = '';
        let isTextContent = false;

        // Disable button
        if (button) {
          button.disabled = true;
          button.style.opacity = '0.5';

          if (button.textContent && button.textContent.trim()) {
            originalText = button.textContent;
            isTextContent = true;
            button.textContent = 'Adding...';
          } else if (button.value) {
            originalText = button.value;
            isTextContent = false;
            button.value = 'Adding...';
          }

          console.log('💾 Stored original button text:', originalText);
        }

        const variantId = formData.get('id');
        console.log('📤 Submitting variant ID:', variantId);

        // Get product handle from form or page to check if excluded
        let productHandle = form.querySelector('[name="product-id"]')?.dataset?.handle;

        if (!productHandle) {
          // Extract from URL - handle /products/handle-name or /products/handle-name/
          const pathParts = window.location.pathname.split('/').filter(p => p);
          const productIndex = pathParts.indexOf('products');
          if (productIndex >= 0 && pathParts[productIndex + 1]) {
            productHandle = pathParts[productIndex + 1];
          }
        }

        console.log('🔍 Product handle:', productHandle);

        // Check if this product is excluded from limiting
        const isExcluded = await isProductExcluded(productHandle);

        if (isExcluded) {
          console.log('✅ Product is excluded from quantity limiting, allowing normal submission');
          // For excluded products, submit normally without restrictions
          return originalFetch('/cart/add.js', {
            method: 'POST',
            body: formData
          })
          .then(response => response.json())
          .then(data => {
            console.log('✅ Excluded product added successfully:', data);

            // Set flag to show confirmation modal (for all products)
            console.log('💾 Setting productAdded flag');
            sessionStorage.setItem('productAdded', 'true');

            // Dispatch event for upsell script (will be ignored for excluded products)
            const event = new CustomEvent('CartItemAdded', {
              detail: { item: data }
            });
            window.dispatchEvent(event);

            // Refresh page (increased timeout to allow async upsell logic to complete)
            setTimeout(() => {
              if (!document.getElementById('unlimited-upsell-overlay')) {
                console.log('💾 No upsell overlay, reloading page...');
                window.location.reload();
              } else {
                console.log('⏭️ Upsell overlay detected, skipping reload');
              }
            }, 500);
          })
          .catch(error => {
            console.log('❌ Error:', error);
            alert('Error adding to cart. Please try again.');

            if (button) {
              button.disabled = false;
              button.style.opacity = '1';
              if (isTextContent) {
                button.textContent = originalText;
              } else {
                button.value = originalText;
              }
            }
            isSubmitting = false;
          });
        }

        // For non-excluded products: Force quantity to 1 and check duplicates
        formData.set('quantity', '1');
        console.log('🔒 Forcing quantity to 1 (not excluded)');

        // FIRST: Check if this item is already in cart
        return originalFetch('/cart.js')
          .then(response => response.json())
          .then(currentCart => {
            console.log('🛒 Current cart:', currentCart);

            const existingItem = currentCart.items.find(item => item.variant_id == variantId);

            if (existingItem) {
              console.log('⛔ BLOCKED: Item already in cart!');

              // Show error message under the button
              const errorMsg = document.createElement('div');
              errorMsg.className = 'cart-limiter-error';
              errorMsg.style.cssText = `
                background: #ffe0e0;
                border: 1px solid #ff6b6b;
                color: #c92a2a;
                padding: 12px 15px;
                margin-top: 10px;
                border-radius: 4px;
                font-size: 14px;
                line-height: 1.5;
                text-align: center;
                width: 100%;
                box-sizing: border-box;
              `;
              errorMsg.innerHTML = `
                <strong>⚠️ Ce cours est déjà dans votre panier.</strong><br>
                Quantité maximale : 1 cours par inscription.
              `;

              // Remove any existing error messages
              const existingError = form.querySelector('.cart-limiter-error');
              if (existingError) {
                existingError.remove();
              }

              // Insert message after the form or button
              if (button && button.parentElement) {
                button.parentElement.appendChild(errorMsg);
              } else {
                form.appendChild(errorMsg);
              }

              // Remove message after 5 seconds
              setTimeout(() => {
                errorMsg.style.transition = 'opacity 0.3s';
                errorMsg.style.opacity = '0';
                setTimeout(() => errorMsg.remove(), 300);
              }, 5000);

              // Re-enable button
              if (button) {
                button.disabled = false;
                button.style.opacity = '1';
                if (isTextContent) {
                  button.textContent = originalText;
                } else {
                  button.value = originalText;
                }
              }

              isSubmitting = false;
              return Promise.reject('Item already in cart');
            }

            console.log('✅ Item not in cart, proceeding to add');

            // Item not in cart, proceed with adding
            return originalFetch('/cart/add.js', {
              method: 'POST',
              body: formData
            });
          })
          .then(response => {
            console.log('📥 Response received:', response.status, response.statusText);
            return response.json();
          })
          .then(data => {
            console.log('✅ Item added successfully:', data);
            visibleLog('✅ Item added! Checking for upsell...');

            // Dispatch event for upsell script
            const event = new CustomEvent('CartItemAdded', {
              detail: { item: data }
            });
            window.dispatchEvent(event);

            // Wait longer to allow upsell to show
            // The upsell script will handle refresh if it shows
            setTimeout(() => {
              // Only refresh if no upsell modal is showing
              if (!document.getElementById('unlimited-upsell-overlay')) {
                console.log('No upsell shown, refreshing...');
                window.location.reload();
              } else {
                console.log('Upsell showing, refresh will be handled by upsell');
              }
            }, 500);
          })
          .catch(error => {
            console.log('❌ Error:', error);

            // Only show alert if it's not the "already in cart" rejection
            if (error !== 'Item already in cart') {
              alert('Error adding to cart. Please try again.');
            }

            if (button) {
              button.disabled = false;
              button.style.opacity = '1';

              if (isTextContent) {
                button.textContent = originalText;
              } else {
                button.value = originalText;
              }
            }

            isSubmitting = false;
          });

        return false;
      };

      // Add listener in capture phase (runs first)
      form.addEventListener('submit', submitHandler, true);

      // Also block all buttons
      const buttons = form.querySelectorAll('button, input[type="submit"], [name="add"]');
      buttons.forEach(btn => {
        if (btn.dataset.protected) return;
        btn.dataset.protected = 'true';

        btn.addEventListener('click', function(e) {
          const now = Date.now();
          if (isSubmitting || (now - lastSubmitTime < DEBOUNCE_TIME)) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            console.log('⛔ Button click blocked');
            return false;
          }
        }, true);
      });
    });
  }

  // Set quantity inputs to max 1 and hide quantity selector on product pages
  async function limitQuantityInputs() {
    // Only run on product pages
    if (!window.location.pathname.includes('/products/')) {
      console.log('ℹ️ Not on product page, skipping quantity limiting');
      return;
    }

    const inputs = document.querySelectorAll('input[name="quantity"]');

    if (inputs.length === 0) {
      console.log('ℹ️ No quantity inputs found');
      return;
    }

    // Get product handle from URL
    let productHandle = null;
    const pathParts = window.location.pathname.split('/').filter(p => p);
    const productIndex = pathParts.indexOf('products');
    if (productIndex >= 0 && pathParts[productIndex + 1]) {
      productHandle = pathParts[productIndex + 1];
    }

    console.log('🔍 Checking exclusion for product:', productHandle);
    visibleLog('🔍 Checking exclusion for: ' + productHandle);
    const isExcluded = await isProductExcluded(productHandle);

    if (isExcluded) {
      console.log('✅ Product excluded from quantity limiting, showing quantity selector');
      visibleLog('✅ EXCLUDED - Showing quantity selector');
      // For excluded products, ensure quantity inputs are visible and functional
      inputs.forEach(input => {
        input.removeAttribute('max');
        input.style.display = '';
        // Make sure parent containers are visible
        let parent = input.parentElement;
        let attempts = 0;
        while (parent && attempts < 5) {
          const parentClass = parent.className || '';
          const parentId = parent.id || '';
          if (
            parentClass.includes('quantity') ||
            parentClass.includes('qty') ||
            parentId.includes('quantity') ||
            parentId.includes('Quantity') ||
            parent.tagName === 'QUANTITY-INPUT'
          ) {
            parent.style.display = '';
            console.log('✅ Ensured quantity selector is visible');
            break;
          }
          parent = parent.parentElement;
          attempts++;
        }
      });
      return; // Don't limit quantities for excluded products
    }

    console.log('🔒 Product NOT excluded - limiting to quantity 1 and hiding selector');
    visibleLog('🔒 NOT EXCLUDED - Hiding qty selector');

    // For non-excluded products: limit to 1 and hide selector
    inputs.forEach(input => {
      input.setAttribute('max', '1');
      input.value = '1';

      input.addEventListener('input', function() {
        if (parseInt(this.value) > 1) this.value = 1;
      });

      input.addEventListener('change', function() {
        if (parseInt(this.value) > 1) this.value = 1;
      });

      // Find and hide the parent quantity selector container
      let parent = input.parentElement;
      let attempts = 0;

      // Search up to 5 levels for the quantity container
      while (parent && attempts < 5) {
        const parentClass = parent.className || '';
        const parentId = parent.id || '';

        // Look for common quantity selector class/id patterns
        if (
          parentClass.includes('quantity') ||
          parentClass.includes('qty') ||
          parentId.includes('quantity') ||
          parentId.includes('Quantity') ||
          parent.tagName === 'QUANTITY-INPUT'
        ) {
          parent.style.display = 'none';
          console.log('✅ Hidden quantity selector container');
          break;
        }

        parent = parent.parentElement;
        attempts++;
      }

      // If we didn't find a container, just hide the input itself
      if (attempts === 5) {
        input.style.display = 'none';
        console.log('✅ Hidden quantity input directly');
      }
    });
  }

  // Initialize
  function init() {
    console.log('🔧 Initializing cart protection...');
    blockDuplicateSubmissions();
    limitQuantityInputs();
    console.log('✅ Cart protection initialized');
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run initialization after a delay to catch any late-loading forms
  setTimeout(() => {
    blockDuplicateSubmissions();
    limitQuantityInputs();
  }, 2000);

  // ============================================================================
  // PRODUCT ADDED CONFIRMATION MODAL
  // ============================================================================
  // Show confirmation modal after page reload if product was added
  function showProductAddedConfirmation() {
    console.log('🔍 Checking for productAdded flag...');
    const flag = sessionStorage.getItem('productAdded');
    console.log('   Flag value:', flag);

    if (flag === 'true') {
      console.log('✅ Showing confirmation modal');
      sessionStorage.removeItem('productAdded');

      // Create overlay
      const overlay = document.createElement('div');
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.6);
        z-index: 999998;
        display: flex;
        align-items: center;
        justify-content: center;
      `;

      // Create modal
      const modal = document.createElement('div');
      modal.style.cssText = `
        background: white;
        padding: 40px;
        border-radius: 12px;
        max-width: 500px;
        width: 90%;
        box-shadow: 0 10px 40px rgba(0,0,0,0.3);
        text-align: center;
        position: relative;
      `;

      modal.innerHTML = `
        <div style="font-size: 48px; margin-bottom: 20px;">✅</div>
        <h2 style="margin: 0 0 15px 0; font-size: 24px; color: #4CAF50;">
          Produit ajouté au panier !
        </h2>
        <p style="font-size: 16px; color: #666; margin-bottom: 25px;">
          Votre produit a été ajouté au panier avec succès.
        </p>
        <button id="close-confirmation" style="
          background: #4CAF50;
          color: white;
          border: none;
          padding: 15px 30px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: bold;
          cursor: pointer;
          transition: background 0.3s;
        ">
          Continuer
        </button>
      `;

      overlay.appendChild(modal);
      document.body.appendChild(overlay);

      // Close button handler
      const closeBtn = document.getElementById('close-confirmation');
      closeBtn.addEventListener('click', () => {
        overlay.remove();
        window.location.reload();
      });

      closeBtn.addEventListener('mouseenter', () => {
        closeBtn.style.background = '#45a049';
      });
      closeBtn.addEventListener('mouseleave', () => {
        closeBtn.style.background = '#4CAF50';
      });

      // Close on overlay click
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          overlay.remove();
          window.location.reload();
        }
      });

      // Auto-close after 3 seconds and reload
      setTimeout(() => {
        if (overlay.parentNode) {
          overlay.remove();
        }
        window.location.reload();
      }, 3000);
    }
  }

  // Check for confirmation modal on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', showProductAddedConfirmation);
  } else {
    showProductAddedConfirmation();
  }

  console.log('✅ Cart Limiter Loaded Successfully');

})();
